"use client"

import { useEffect, useState, useContext } from "react"
import api from "../services/api"
import DashboardLayout from "../components/Layout/DashboardLayout"
import ActionButtons from "../components/links/ActionButtons"
import { AuthContext } from "../context/AuthContext"
import CreateLinkModal from "../components/links/CreateLinkModal"
import AnalyticsModal from "../components/Analytics/AnalyticsModal"
import PremiumBadge from "../components/Dashboard/PremiumBadge"
import ToastNotification from "../components/Dashboard/ToastNotification"
import ConfirmModal from "../components/links/ConfirmModal"
import MiniFooter from "../components/Layout/miniFooter"
import Breadcrumbs from "../components/Layout/Breadcrumbs"
import { useNavigate } from "react-router-dom"
import { FaQrcode } from "react-icons/fa"
import WhatsAppChat from "../components/support/WhatsAppChat"

export default function MyLinks() {
  const { user: authUser } = useContext(AuthContext)
  const [user, setUser] = useState(authUser)
  const nav = useNavigate()

  const [refresh, setRefresh] = useState(false)
  const [links, setLinks] = useState([])
  const [loading, setLoading] = useState(true)
  const [copiedId, setCopiedId] = useState(null)
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrImages, setQrImages] = useState({});


  const [lockedFeatures, setLockedFeatures] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(false)
  const [selectedLink, setSelectedLink] = useState(null)
  const [analyticsData, setAnalyticsData] = useState(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)

  const [showPremiumAlert, setShowPremiumAlert] = useState(false)
  const [toastMessage, setToastMessage] = useState("")

  const [confirmState, setConfirmState] = useState({
    show: false,
    payload: null,
  })
  const [deleting, setDeleting] = useState(false)

  const breadcrumbItems = [{ name: "Dashboard", path: "/user-dashboard" }, { name: "All Short URLs" }]

    useEffect(() => {
      api
        .get("/user/me")
        .then((res) => setUser(res.data.data))
        .catch(() => {})
    }, [])

  useEffect(() => {
    loadLinks()
  }, [refresh])

  const loadLinks = async () => {
    setLoading(true)
    try {
      const res = await api.get("/links/v1-n")
      const data = res.data?.data
      setLinks(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch QR whenever selectedLink changes
  useEffect(() => {
    if (!selectedLink) return;

    const fetchQr = async () => {
      setQrLoading(true);
      setQrImages({});
      setLockedFeatures(null);

      try {
        const formats = ['PNG', 'SVG', 'PDF'];
        const qrData = {};
        let locked = null;

        for (const fmt of formats) {
          const res = await api.get(`/qr/img/${selectedLink.shortCode}?format=${fmt.toLowerCase()}`);
          if (res.data.success) {
            qrData[fmt.toLowerCase()] = res.data.qr;
            if (!locked) locked = res.data.lockedFeatures;
          }
        }

        setQrImages(qrData);
        setLockedFeatures(locked);
      } catch (err) {
        console.error(err);
        setQrImages({});
        setLockedFeatures(null);
      } finally {
        setQrLoading(false);
      }
    };

    fetchQr();

    return () => {
      setQrImages({});
      setLockedFeatures(null);
    };
  }, [selectedLink]);

  const copyToClipboard = async (link) => {
  const url = resolveShortUrl(link)
  await navigator.clipboard.writeText(url)
  
  setCopiedId(link.id)
  setToastMessage("Link copied successfully!")
  setTimeout(() => setCopiedId(null), 1500)
}


  const requestDelete = (id) => {
    setConfirmState({
      show: true,
      payload: id,
    })
  }

  const confirmDelete = async () => {
    if (!confirmState.payload) return

    setDeleting(true)

    try {
      await api.delete(`/links/${confirmState.payload}`)
      setToastMessage("Link deleted successfully!")
      setRefresh((prev) => !prev)
    } catch (err) {
      console.error(err)
    } finally {
      setDeleting(false)
      setConfirmState({ show: false, payload: null })
    }
  }

  const handleLinkCreated = () => {
    setRefresh((prev) => !prev)
    setToastMessage("Link created successfully!")
  }

  const openAnalytics = async (link) => {
    setSelectedLink(link)
    setShowAnalytics(true)
    setAnalyticsLoading(true)
    setAnalyticsData(null)

    try {
      const res = await api.get(`/links/analytics/${link.shortCode}`)
      const data = res.data.data || {}      

      const safeAnalyticsData = {
        linkInfo: {
          shortCode: data.linkInfo?.shortCode || link.shortCode,
          originalUrl: data.linkInfo?.originalUrl || link.originalUrl,
          isActive: data.linkInfo?.isActive ?? false,
          expiresAt: data.linkInfo?.expiresAt || null,
          createdAt: data.linkInfo?.createdAt || link.createdAt,
        },
        plan: data.plan || user?.plan || "free",
        totalClicks: data.totalClicks || 0,
        totalQrScans: data.totalQrScans || 0,
        daily: data.daily || [],
      }
      

      setAnalyticsData(safeAnalyticsData)
    } catch (err) {
      console.error("Failed to fetch analytics", err)
      setAnalyticsData({
        linkInfo: {
          shortCode: link.shortCode,
          originalUrl: link.originalUrl,
          isActive: false,
          expiresAt: null,
          createdAt: link.createdAt,
        },
        plan: user?.plan || "free",
        totalClicks: link.clicks || 0,
        totalQrScans: link.qrScans || 0,
        daily: [],
      })
    } finally {
      setAnalyticsLoading(false)
    }
  }

const handleEditClick = (link) => {
  const allowedPlans = ["standard", "premium", "dedicated"]

  if (!allowedPlans.includes(user?.plan)) {
    setShowPremiumAlert(true)
    setTimeout(() => setShowPremiumAlert(false), 3000)
    return
  }

  nav(`/dashboard/urls/${link._id}`)
}

  const resolveShortUrl = (link) => {
  // const plan = user?.plan?.toLowerCase() || "free"

  // Free plan → platform domain
  // if (plan === "free") {
  //   return `${window.location.origin}/s/${link.shortCode}`
  // }

  // Paid plans → custom/branded domain
  return link.shortUrl || `${window.location.origin}/${link.shortCode}`
}


  // Skeleton row component
  const SkeletonRow = () => (
    <tr>
      {[...Array(6)].map((_, i) => (
        <td key={i} style={{ padding: "1rem 1.25rem" }}>
          <div
            style={{
              height: i === 5 ? "32px" : "20px",
              width: i === 0 ? "180px" : i === 1 ? "200px" : i === 5 ? "120px" : "80px",
              backgroundColor: "#e5e7eb",
              borderRadius: "6px",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          />
        </td>
      ))}
    </tr>
  )

  return (
    <DashboardLayout>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          @keyframes slideIn {
            from { opacity: 0; transform: translateY(-12px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .my-links-container {
            background-color: #f8fafc;
            min-height: calc(100vh - 100px);
          }

          .page-header {
            background-color: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 1.5rem 2rem;
            margin-bottom: 1.5rem;
          }

          .page-title {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 700;
            color: #0f172a;
          }

          .page-subtitle {
            margin: 0.25rem 0 0 0;
            font-size: 0.875rem;
            color: #64748b;
          }

          .new-link-btn {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            background-color: #3b82f6;
            color: #ffffff;
            border: none;
            padding: 0.625rem 1.25rem;
            border-radius: 8px;
            font-weight: 600;
            font-size: 0.875rem;
            transition: all 0.2s ease;
          }

          .new-link-btn:hover {
            background-color: #2563eb;
            color: #ffffff;
          }

          .links-card {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            overflow: hidden;
          }

          .table-header th {
            background-color: #f8fafc;
            font-weight: 600;
            color: #475569;
            text-transform: uppercase;
            font-size: 0.7rem;
            letter-spacing: 0.05em;
            border-bottom: 1px solid #e2e8f0;
            padding: 0.875rem 1.25rem;
          }

          .table-row {
            transition: background-color 0.15s ease;
            border-bottom: 1px solid #f1f5f9;
          }

          .table-row:last-child {
            border-bottom: none;
          }

          .table-row:hover {
            background-color: #f8fafc;
          }

          .short-link-container {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .link-icon {
            width: 32px;
            height: 32px;
            background-color: #eff6ff;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .short-link-text {
            color: #3b82f6;
            font-weight: 600;
            font-size: 0.875rem;
          }

          .original-url {
            color: #64748b;
            font-size: 0.8125rem;
          }

          .clicks-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            background-color: #f1f5f9;
            color: #334155;
            padding: 0.375rem 0.75rem;
            border-radius: 6px;
            font-weight: 600;
            font-size: 0.8125rem;
          }

          .status-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.375rem 0.75rem;
            border-radius: 6px;
            font-weight: 500;
            font-size: 0.75rem;
          }

          .status-active {
            background-color: #dcfce7;
            color: #166534;
          }

          .status-disabled {
            background-color: #f1f5f9;
            color: #64748b;
          }

          .status-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
          }

          .status-dot-active {
            background-color: #22c55e;
          }

          .status-dot-disabled {
            background-color: #94a3b8;
          }

          .date-text {
            color: #64748b;
            font-size: 0.8125rem;
          }

          .empty-state {
            text-align: center;
            padding: 4rem 2rem;
          }

          .empty-icon-wrapper {
            width: 80px;
            height: 80px;
            background-color: #f1f5f9;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1.5rem;
          }

          .empty-title {
            font-size: 1.125rem;
            font-weight: 600;
            color: #0f172a;
            margin-bottom: 0.5rem;
          }

          .empty-text {
            color: #64748b;
            font-size: 0.875rem;
            margin-bottom: 1.5rem;
          }

          .loading-state {
            text-align: center;
            padding: 3rem 2rem;
          }

          .premium-alert-wrapper {
            animation: slideIn 0.3s ease;
            margin-bottom: 1rem;
          }

          .stats-row {
            display: flex;
            gap: 1rem;
            margin-bottom: 1.5rem;
          }

          .stat-card {
            flex: 1;
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 1.25rem;
            display: flex;
            align-items: center;
            gap: 1rem;
          }

          .stat-icon {
            width: 44px;
            height: 44px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .stat-icon-blue {
            background-color: #eff6ff;
            color: #3b82f6;
          }

          .stat-icon-green {
            background-color: #dcfce7;
            color: #22c55e;
          }

          .stat-icon-amber {
            background-color: #fef3c7;
            color: #f59e0b;
          }

          .stat-value {
            font-size: 1.5rem;
            font-weight: 700;
            color: #0f172a;
            line-height: 1;
          }

          .stat-label {
            font-size: 0.75rem;
            color: #64748b;
            margin-top: 0.25rem;
          }
        `}
      </style>

      <div className="my-links-container">
        <div className="container py-4">
          <Breadcrumbs items={breadcrumbItems} />

          {/* Page Header */}
          <div className="page-header">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h3 className="page-title">My Short URLs</h3>
                <p className="page-subtitle">Manage and track all your shortened links</p>
              </div>
              <button className="new-link-btn" onClick={() => setShowCreateModal(true)}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                New Link
              </button>
            </div>
          </div>

          {/* Premium Alert */}
          {showPremiumAlert && (
            <div className="premium-alert-wrapper">
              <PremiumBadge user={user} message="Upgrade to Premium to unlock this feature!" visible />
            </div>
          )}

          {/* Stats Row */}
          {!loading && links.length > 0 && (
            <div className="stats-row">
              <div className="stat-card">
                <div className="stat-icon stat-icon-blue">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                </div>
                <div>
                  <div className="stat-value">{links.length}</div>
                  <div className="stat-label">Total Links</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon stat-icon-green">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                </div>
                <div>
                  <div className="stat-value">{links.reduce((acc, link) => acc + (link.clicks || 0), 0)}</div>
                  <div className="stat-label">Total Clicks</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon stat-icon-amber">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div>
                  <div className="stat-value">{links.filter((l) => l.isActive).length}</div>
                  <div className="stat-label">Active Links</div>
                </div>
              </div>
            </div>
          )}

          {/* Links Table */}
          <div className="links-card">
            {loading ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr className="table-header">
                      <th>Short Link</th>
                      <th>Original URL</th>
                      <th>Clicks</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>QR</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                  </tbody>
                </table>
              </div>
            ) : links.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon-wrapper">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#94a3b8"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                </div>
                <h5 className="empty-title">No links yet</h5>
                <p className="empty-text">Create your first shortened link to get started</p>
                <button className="new-link-btn" onClick={() => setShowCreateModal(true)}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Create Link
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr className="table-header">
                      <th>Short Link</th>
                      <th>Original URL</th>
                      <th>Clicks</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Generate</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {links.map((link) => {
                      const shortUrl = resolveShortUrl(link)
                      const canUseQR = ["basic", "standard", "premium", "dedicated"].includes(user?.plan);


                      return (
                        <tr key={link._id} className="table-row">
                          <td style={{ padding: "1rem 1.25rem" }}>
                            <div className="short-link-container">
                              <div className="link-icon">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#3b82f6"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                </svg>
                              </div>
                              <span className="short-link-text">{shortUrl}</span>
                            </div>
                          </td>
                          <td style={{ padding: "1rem 1.25rem" }}>
                            <div
                              className="original-url text-truncate"
                              style={{ maxWidth: 240 }}
                              title={link.originalUrl}
                            >
                              {link.originalUrl}
                            </div>
                          </td>
                          <td style={{ padding: "1rem 1.25rem" }}>
                            <span className="clicks-badge">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                                <polyline points="10 17 15 12 10 7" />
                                <line x1="15" y1="12" x2="3" y2="12" />
                              </svg>
                              {link.clicks}
                            </span>
                          </td>
                          <td style={{ padding: "1rem 1.25rem" }}>
                            {link.isActive ? (
                              <span className="status-badge status-active">
                                <span className="status-dot status-dot-active" />
                                Active
                              </span>
                            ) : (
                              <span className="status-badge status-disabled">
                                <span className="status-dot status-dot-disabled" />
                                Disabled
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "1rem 1.25rem" }}>
                            <span className="date-text">{new Date(link.createdAt).toLocaleDateString()}</span>
                          </td>
                          <td>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => {
                                if (!canUseQR) {
                                  // show alert for free users
                                  setShowPremiumAlert(true);
                                  setTimeout(() => setShowPremiumAlert(false), 5000);
                                  return;
                                }

                                // allowed plans
                                setSelectedLink(link);
                                setShowQrModal(true);
                              }}
                            >
                              <FaQrcode /> QR code
                            </button>

                                            </td>
                          <td style={{ padding: "1rem 1.25rem" }} className="text-end">
                            <ActionButtons
                              link={link}
                              shortUrl={shortUrl}
                              user={user}
                              copied={copiedId === link._id}
                              onCopy={() => copyToClipboard(link)}
                              onDelete={() => requestDelete(link._id)}
                              onAnalytics={() => openAnalytics(link)}
                              onEdit={() => handleEditClick(link)}
                              loadingAnalytics={analyticsLoading && selectedLink?._id === link._id}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateLinkModal show={showCreateModal} onClose={() => setShowCreateModal(false)} onCreated={handleLinkCreated} />

      <AnalyticsModal show={showAnalytics} analyticsData={analyticsData} onClose={() => setShowAnalytics(false)} />

{/* QR Modal */}
{showQrModal && (
  <div
    className="modal fade show d-block"
    style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
    onClick={() => setShowQrModal(false)}
  >
    <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
      <div className="modal-content text-center p-4">
        <h5 className="mb-3">QR Code Preview</h5>

        {qrLoading && <p>Generating QR code...</p>}

        {/* {lockedFeatures && Object.values(lockedFeatures).some(v => v) && (
          <div className="alert alert-warning py-2">
            Some QR features are locked for your plan. Upgrade to access full customization.
          </div>
        )} */}

        {!qrLoading && qrImages && (
          <div className="mb-3">
            {/* Show PNG as main preview */}
            {qrImages.png && (
              <img
                src={qrImages.png}
                alt="QR Code"
                className="img-fluid mb-3"
                style={{ maxHeight: 300 }}
              />
            )}
            
            {/* Dynamically render download buttons side by side */}
            <div className="d-flex justify-content-center gap-2 flex-wrap">
              {Object.entries(qrImages).map(([format, url]) => {
                if (!url) return null; // skip empty
                if (lockedFeatures?.[format]) return null; // skip locked

                const btnClass =
                  format === 'png' ? 'btn-outline-success' :
                  format === 'svg' ? 'btn-outline-warning' :
                  format === 'pdf' ? 'btn-outline-info' :
                  'btn-outline-secondary';

                const downloadName = `qr.${format}`;
                const href = format === 'svg' ? `data:image/svg+xml;utf8,${encodeURIComponent(url)}` : url;

                return (
                  <a
                    key={format}
                    href={href}
                    download={downloadName}
                    className={`btn btn-sm ${btnClass}`}
                  >
                    Download {format.toUpperCase()}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        <button className="btn btn-sm btn-secondary mt-3" onClick={() => setShowQrModal(false)}>Close</button>
      </div>
    </div>
  </div>
)}


      <ConfirmModal
        show={confirmState.show}
        title="Confirm deletion"
        message="Are you sure you want to delete this link?"
        confirmText="Delete"
        loading={deleting}
        onCancel={() => setConfirmState({ show: false, payload: null })}
        onConfirm={confirmDelete}
      />

      <ToastNotification message={toastMessage} duration={3000} onClose={() => setToastMessage("")} />
      <WhatsAppChat position="right" />
      <MiniFooter />
    </DashboardLayout>
  )
}
